import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {makeApolloClientCall, makeApolloClientMutationCall} from "../../Api/makeApolloApiCall";
import {makeApiCallPost} from "../../Api/makeApiCall";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import ReactTable from "../Components/ReactTable";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import ConfirmationModal from "../Components/ConfirmationModal";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {toastError, toastSuccess} from "../../utils/toast";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "./Components/VersionSelect";
import {
    DATE_EXPRESSION_COLUMN,
    formatRecommendedUniverse,
    NUMBER_EXPRESSION_COLUMN,
} from "./Components/storeUniverseTableHelpers";
import {useProjectionLoadTimer} from "./Components/ProjectionLoadTimer";

export const PERIODS_QUERY = 'query Query { periods(periodEndWeeksOnly:false excludeFuture:true) { endDate adpStart } }';

export const STORE_UNIVERSE_SUMMARY_QUERY = `
    query GetStoreUniverseSummary($weekEndDates: [String!]!, $version: String) {
        storeUniverseSummary(weekEndDates: $weekEndDates, version: $version) {
            universe
            cellId
            isCooperator
            sampled
            storeCount
            acv
            geographicRegion
            marketTag
            marketName
            marketId
            week
        }
    }
`;

export const CREATE_PROJECTION_VERSION_QUERY = `
    mutation($version: String!) {
        createProjectionVersion(version: $version) {
            version
        }
    }
`;

export const COPY_PROJECTION_VERSION_QUERY = `
    mutation($fromVersion: String!, $toVersion: String!) {
        copyProjectionVersion(fromVersion: $fromVersion, toVersion: $toVersion) {
            fromVersion
            toVersion
            nodesCopied
            relationshipsCopied
            relationshipsSkipped
        }
    }
`;

export const DELETE_PROJECTION_VERSION_QUERY = `
    mutation($version: String!) {
        deleteProjectionVersion(version: $version) {
            version
            nodesDeleted
            relationshipsDeleted
            filePathsDeleted
        }
    }
`;

export const serverErrorMessage = (error) =>
    String(error?.message ?? error).replace(/^(Error:\s*|ApolloError:\s*)+/g, '');

export const latestWeekEndDateFromPeriods = (periods = []) => {
    const adpStartPeriods = periods.filter(period => period.adpStart);
    const earliestAdpStart = adpStartPeriods.reduce((earliest, period) =>
        !earliest || new Date(period.endDate) < new Date(earliest.endDate) ? period : earliest, undefined);

    return (earliestAdpStart
        ? periods.filter(period => new Date(period.endDate) >= new Date(earliestAdpStart.endDate))
        : periods)
        .map(period => period.endDate)
        .sort()
        .reverse()[0];
};

const sumColumn = (accessorKey, format = (total) => total) => ({table}) =>
    <span>{format(table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original[accessorKey] ?? 0), 0))}</span>;

const formatAcv = (acv) => acv.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

const summaryColumns = [
    {
        header: 'UNIVERSE',
        accessorKey: 'universe',
        accessorFn: (row) => formatRecommendedUniverse(row.universe),
        filterFn: 'includesString',
    },
    {header: 'CELL ID', accessorKey: 'cellId', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'IS COOPERATOR', accessorKey: 'isCooperator', cell: ({getValue}) => String(getValue())},
    {header: 'SAMPLED', accessorKey: 'sampled', cell: ({getValue}) => String(getValue())},
    {
        header: 'STORE COUNT',
        accessorKey: 'storeCount',
        ...NUMBER_EXPRESSION_COLUMN,
        footer: sumColumn('storeCount'),
    },
    {
        header: 'ACV',
        accessorKey: 'acv',
        ...NUMBER_EXPRESSION_COLUMN,
        footer: sumColumn('acv', formatAcv),
    },
    {header: 'REGION', accessorKey: 'geographicRegion', filterFn: 'includesString'},
    {header: 'MARKET TAG', accessorKey: 'marketTag', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'MARKET NAME', accessorKey: 'marketName', filterFn: 'includesString'},
    {header: 'MARKET ID', accessorKey: 'marketId', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'WEEK', accessorKey: 'week', ...DATE_EXPRESSION_COLUMN},
];

export const ProjectionMaintenanceProjectionTab = () => {
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [projectionVersionOptions, setProjectionVersionOptions] = useState(undefined);
    const [projectionVersion, setProjectionVersion] = useState(undefined);

    const [weekEndDate, setWeekEndDate] = useState(undefined);

    const [submittedFilters, setSubmittedFilters] = useState(null);

    const [createVersionModalOpen, setCreateVersionModalOpen] = useState(false);
    const [newVersionName, setNewVersionName] = useState('');
    const [creating, setCreating] = useState(false);

    const [copyVersionModalOpen, setCopyVersionModalOpen] = useState(false);
    const [copyToVersion, setCopyToVersion] = useState('');
    const [copying, setCopying] = useState(false);

    const [deleteVersionModalOpen, setDeleteVersionModalOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const {startLoad, finishLoad, cancelLoad} = useProjectionLoadTimer();

    useEffect(() => {
        makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY)
            .then((projectionVersionsResponse) => {
                setProjectionVersionOptions(projectionVersionsResponse.availableProjectionVersions
                    .map(version => wrapValueInOptionStructure(version.version)));
            })
            .catch(() => setProjectionVersionOptions([]));

        makeApolloClientCall(PERIODS_QUERY, true)
            .then((periodsResponse) => {
                setWeekEndDate(previous => previous || latestWeekEndDateFromPeriods(periodsResponse?.periods));
            });
    }, []);

    const onSubmit = () => {
        setSubmittedFilters({projectionVersion, weekEndDate});
    };

    const closeCreateVersionModal = () => {
        setCreateVersionModalOpen(false);
        setNewVersionName('');
    };

    const onCreateProjectionVersionConfirmed = () => {
        const version = newVersionName.trim();

        setCreating(true);
        makeApolloClientMutationCall(CREATE_PROJECTION_VERSION_QUERY, {version})
            .then(() => {
                toastSuccess(toast, `Created ${version}. It is empty until stores or a universe are copied or imported into it.`);
                setProjectionVersionOptions(previousOptions => (previousOptions ?? []).some(option => option.value === version)
                    ? previousOptions
                    : [...(previousOptions ?? []), wrapValueInOptionStructure(version)]);
                setProjectionVersion(version);
                setSubmittedFilters(null);
                closeCreateVersionModal();
            })
            .catch((error) => toastError(toast, `Failed to create ${version}: ${serverErrorMessage(error)}`))
            .finally(() => setCreating(false));
    };

    const onCopyProjectionVersionConfirmed = () => {
        const toVersion = copyToVersion.trim();

        setCopying(true);
        makeApolloClientMutationCall(COPY_PROJECTION_VERSION_QUERY, {fromVersion: projectionVersion, toVersion})
            .then((copyResponse) => {
                const {nodesCopied, relationshipsCopied, relationshipsSkipped} = copyResponse.copyProjectionVersion;
                toastSuccess(toast, `Copied ${projectionVersion} to ${toVersion}: ${nodesCopied} nodes, ${relationshipsCopied} relationships copied, ${relationshipsSkipped} skipped`);
                setProjectionVersionOptions(previousOptions => previousOptions.some(option => option.value === toVersion)
                    ? previousOptions
                    : [...previousOptions, wrapValueInOptionStructure(toVersion)]);
                setCopyVersionModalOpen(false);
                setCopyToVersion('');
            })
            .catch((error) => toastError(toast, `Failed to copy ${projectionVersion} to ${toVersion}: ${serverErrorMessage(error)}`))
            .finally(() => setCopying(false));
    };

    const closeDeleteVersionModal = () => {
        setDeleteVersionModalOpen(false);
        setDeleteConfirmText('');
    };

    const onDeleteProjectionVersionConfirmed = () => {
        const versionToDelete = projectionVersion;

        setDeleting(true);
        makeApiCallPost(`${process.env.REACT_APP_EDP_API_BASE_URL}/auditProjectionVersionDelete`, {version: versionToDelete})
            .catch((error) => ({apiError: error}))
            .then((auditResponse) => {
                if (auditResponse?.apiError) {
                    toastError(toast, `Failed to delete ${versionToDelete}: ${JSON.stringify(auditResponse.apiError)}`);
                    return;
                }
                return makeApolloClientMutationCall(DELETE_PROJECTION_VERSION_QUERY, {version: versionToDelete})
                    .then((deleteResponse) => {
                        const {nodesDeleted, relationshipsDeleted, filePathsDeleted} = deleteResponse.deleteProjectionVersion;
                        toastSuccess(toast, `Deleted ${versionToDelete}: ${nodesDeleted} nodes, ${relationshipsDeleted} relationships, ${filePathsDeleted.length} storage folders`);
                        setProjectionVersionOptions(previousOptions =>
                            previousOptions.filter(option => option.value !== versionToDelete));
                        setProjectionVersion(undefined);
                        setSubmittedFilters(null);
                        closeDeleteVersionModal();
                    })
                    .catch((error) => toastError(toast, `Failed to delete ${versionToDelete}: ${serverErrorMessage(error)}`));
            })
            .finally(() => setDeleting(false));
    };

    useEffect(() => {
        if (!submittedFilters) return;

        setLoading(true);
        startLoad('Projection tab');
        makeApolloClientCall(STORE_UNIVERSE_SUMMARY_QUERY, false, {
            weekEndDates: [submittedFilters.weekEndDate],
            version: submittedFilters.projectionVersion,
        })
            .then((summaryResponse) => {
                finishLoad();
                setSummaryData(summaryResponse?.storeUniverseSummary ?? []);
            })
            .catch(() => {
                setSummaryData([]);
                toastError(toast, `Failed to load the universe summary for ${submittedFilters.projectionVersion}`);
            })
            .finally(() => {
                cancelLoad();
                setLoading(false);
            });
    }, [submittedFilters]);

    return (
        <div className={"projection-maintenance-projection-contents"}>
            <div className={'projection-toolbar'} data-testid={'projection_factors_version'}>
                <SelectDropdown id={"projection_version_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"projection_version"}
                                inputLabel={"Projection Version"}
                                selectValue={projectionVersion}
                                options={projectionVersionOptions}
                                onChange={(option) => setProjectionVersion(option?.value)}
                />

                <button type={'button'}
                        className={'button primary projection-summary-submit-button'}
                        data-testid={'projection-summary-submit-button'}
                        disabled={!projectionVersion || !weekEndDate}
                        title={projectionVersion && weekEndDate ? 'Load the universe summary for the selected version and latest week' : 'Select a Projection Version first'}
                        onClick={onSubmit}
                >
                    Submit
                </button>

                <button type={'button'}
                        className={'button primary projection-create-version-button'}
                        data-testid={'create-projection-version-button'}
                        title={'Create a new, empty projection version'}
                        onClick={() => setCreateVersionModalOpen(true)}
                >
                    Create Projection Version
                </button>

                <button type={'button'}
                        className={'button primary projection-copy-version-button'}
                        data-testid={'copy-projection-version-button'}
                        disabled={!projectionVersion}
                        title={projectionVersion ? 'Copy the selected projection version to another version' : 'Select a Projection Version first'}
                        onClick={() => setCopyVersionModalOpen(true)}
                >
                    Copy Projection Version
                </button>

                <button type={'button'}
                        className={'button alert projection-delete-version-button'}
                        data-testid={'delete-projection-version-button'}
                        disabled={!projectionVersion}
                        title={projectionVersion ? 'Permanently delete the selected projection version' : 'Select a Projection Version first'}
                        onClick={() => setDeleteVersionModalOpen(true)}
                >
                    Delete Projection Version
                </button>
            </div>

            {submittedFilters && <LoadingWrapper loading={loading}>
                <ReactTable className={'projection-universe-summary-table'}
                            filterable
                            data={summaryData}
                            columns={summaryColumns}
                            initialState={{pagination: {pageSize: 25}}}
                />
            </LoadingWrapper>}

            {createVersionModalOpen && <ConfirmationModal headerText={'Create Projection Version'}
                                                          confirmButtonText={'Create'}
                                                          loading={creating}
                                                          showExitButton={false}
                                                          submitButtonDisabled={!newVersionName.trim()}
                                                          confirmationCallback={onCreateProjectionVersionConfirmed}
                                                          cancellationCallback={closeCreateVersionModal}
            >
                <div className={'copy-projection-version-field'}>
                    <label className={'input-group-label'} htmlFor={'create-projection-version'}>Version:</label>
                    <input id={'create-projection-version'}
                           data-testid={'create-projection-version-input'}
                           type={'text'}
                           autoComplete={'off'}
                           spellCheck={false}
                           value={newVersionName}
                           onChange={(e) => setNewVersionName(e.target.value)}
                    />
                </div>
            </ConfirmationModal>}

            {copyVersionModalOpen && <ConfirmationModal headerText={'Copy Projection Version'}
                                                        confirmButtonText={'Copy'}
                                                        loading={copying}
                                                        showExitButton={false}
                                                        submitButtonDisabled={!copyToVersion.trim()}
                                                        confirmationCallback={onCopyProjectionVersionConfirmed}
                                                        cancellationCallback={() => setCopyVersionModalOpen(false)}
            >
                <div className={'copy-projection-version-field'}>
                    <label className={'input-group-label'} htmlFor={'copy-projection-version'}>Version:</label>
                    <input id={'copy-projection-version'}
                           data-testid={'copy-projection-version-input'}
                           type={'text'}
                           value={copyToVersion}
                           onChange={(e) => setCopyToVersion(e.target.value)}
                    />
                </div>
            </ConfirmationModal>}

            {deleteVersionModalOpen && <ConfirmationModal headerText={'Delete Projection Version'}
                                                          modalClassName={'Modal delete-version-modal'}
                                                          confirmButtonText={'Delete Forever'}
                                                          confirmButtonClass={'alert'}
                                                          loading={deleting}
                                                          showExitButton={false}
                                                          submitButtonDisabled={deleteConfirmText.trim() !== projectionVersion}
                                                          confirmationCallback={onDeleteProjectionVersionConfirmed}
                                                          cancellationCallback={closeDeleteVersionModal}
            >
                <div className={'delete-version-body'}>
                    <p className={'delete-version-lead'}>
                        {'Deleting '}
                        <span className={'version-chip'}>{projectionVersion}</span>
                        {' removes:'}
                    </p>
                    <ul className={'delete-version-consequences'}>
                        <li>{'Every node and relationship in the projections graph'}</li>
                        <li>{'Its projection files in cloud storage'}</li>
                    </ul>
                    <p className={'delete-version-danger'}>
                        {'This cannot be undone. There is no backup.'}
                    </p>
                    <label className={'delete-version-confirm-label'} htmlFor={'delete-projection-version'}>
                        {'Type '}
                        <span className={'version-chip'}>{projectionVersion}</span>
                        {' to confirm'}
                    </label>
                    <input id={'delete-projection-version'}
                           data-testid={'delete-projection-version-input'}
                           className={'delete-version-confirm-input'}
                           type={'text'}
                           autoComplete={'off'}
                           spellCheck={false}
                           value={deleteConfirmText}
                           onChange={(e) => setDeleteConfirmText(e.target.value)}
                    />
                </div>
            </ConfirmationModal>}
        </div>
    );
};
